import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import { AlertCircle, Eye, EyeOff, Loader2, Mail } from 'lucide-react';

import { useAuthStore } from '../../store/authStore';
import { getErrorMessage } from '../../utils/error';
import { authService } from '../../services/authService';
import { mcpService } from '../../services/mcpService';
import { useToast } from '../../components/common/Toast';
import { useLanguage } from '../../features/language/useLanguage';

const buildLoginSchema = (t: (key: string, params?: Record<string, string | number>, fallback?: string) => string) =>
  z.object({
    email: z.string().trim().email(t('auth.validation.invalidEmail')),
    password: z.string().min(1, t('auth.validation.passwordRequired')),
  });

type LoginFormData = z.infer<ReturnType<typeof buildLoginSchema>>;

type RedirectState = {
  from?: {
    pathname?: string;
    search?: string;
    hash?: string;
  };
};

const sanitizeRedirectPath = (value?: string | null): string | null => {
  if (!value || !value.startsWith('/') || value.startsWith('//')) {
    return null;
  }

  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) {
      return null;
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
};

export const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const { isAuthenticated, login } = useAuthStore();
  const { showToast } = useToast();
  const loginSchema = useMemo(() => buildLoginSchema(t), [t]);
  const redirectTarget = useMemo(() => {
    const redirectFromQuery = sanitizeRedirectPath(new URLSearchParams(location.search).get('redirect'));
    if (redirectFromQuery) {
      return redirectFromQuery;
    }

    const fromState = (location.state as RedirectState | null)?.from;
    return sanitizeRedirectPath(
      fromState ? `${fromState.pathname ?? ''}${fromState.search ?? ''}${fromState.hash ?? ''}` : null,
    ) ?? '/';
  }, [location.search, location.state]);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onTouched',
  });

  if (isAuthenticated) {
    return <Navigate to={redirectTarget} replace />;
  }

  const onSubmit = async (data: LoginFormData) => {
    try {
      const response = await authService.login(data.email, data.password);
      const { user: bUser, company, token, permissions, company_can_edit_roles } = response;

      login(
        {
          id: bUser.id,
          email: bUser.email,
          name: bUser.full_name,
          role: bUser.role,
          companyId: bUser.company_id,
          emailNotificationsEnabled: bUser.email_notifications_enabled ?? false,
        },
        company,
        token,
        permissions,
        company_can_edit_roles,
      );

      if (response.mcp_api_key) {
        mcpService.setActiveKey(response.mcp_api_key);
      }

      showToast(t('auth.toast.welcomeBack'), 'success');
      navigate(redirectTarget, { replace: true });
    } catch (error) {
      const message = getErrorMessage(error);
      setError('root', { message });
      showToast(message, 'error');
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="mb-8">
        <h2 className="text-[28px] leading-tight font-semibold tracking-tight text-gray-900">
          {t('auth.login.title')}
        </h2>
        <p className="mt-2 text-sm text-gray-500">
          {t('auth.login.newHere')}{' '}
          <Link
            to="/register"
            className="font-medium text-primary-600 underline-offset-4 hover:underline transition-colors"
          >
            {t('auth.login.registerCompany')}
          </Link>
        </p>
      </div>

      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
        {errors.root && (
          <div
            role="alert"
            className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50/80 px-3.5 py-3 text-sm text-red-700"
          >
            <AlertCircle size={16} className="mt-0.5 shrink-0 text-red-500" />
            <span className="leading-relaxed">{errors.root.message}</span>
          </div>
        )}

        <div className="space-y-1.5">
          <label htmlFor="email" className="block text-xs font-medium text-gray-700 tracking-wide">
            {t('auth.login.emailAddress')}
          </label>
          <div className="relative group">
            <Mail
              size={16}
              className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 transition-colors ${
                errors.email ? 'text-red-400' : 'text-gray-400 group-focus-within:text-primary-600'
              }`}
            />
            <input
              {...register('email')}
              id="email"
              type="email"
              autoComplete="email"
              placeholder={t('auth.login.emailPlaceholder')}
              className={`block w-full rounded-lg border bg-white pl-9 pr-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm transition-all focus:outline-none focus:ring-4 ${
                errors.email
                  ? 'border-red-400 focus:border-red-500 focus:ring-red-500/10'
                  : 'border-gray-200 focus:border-primary-500 focus:ring-primary-500/10'
              }`}
            />
          </div>
          {errors.email && (
            <p className="text-xs text-red-600">{errors.email.message}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label htmlFor="password" className="block text-xs font-medium text-gray-700 tracking-wide">
              {t('auth.login.password')}
            </label>
            <Link
              to="/forgot-password"
              className="text-xs font-medium text-gray-500 hover:text-primary-600 transition-colors"
            >
              {t('auth.login.forgotPassword')}
            </Link>
          </div>
          <div className="relative">
            <input
              {...register('password')}
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder={t('auth.login.passwordPlaceholder')}
              className={`block w-full rounded-lg border bg-white px-3 py-2.5 pr-10 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm transition-all focus:outline-none focus:ring-4 ${
                errors.password
                  ? 'border-red-400 focus:border-red-500 focus:ring-red-500/10'
                  : 'border-gray-200 focus:border-primary-500 focus:ring-primary-500/10'
              }`}
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="text-xs text-red-600">{errors.password.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="group relative mt-2 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-b from-primary-500 to-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_1px_0_0_rgba(255,255,255,0.15)_inset,0_1px_2px_0_rgba(2,132,199,0.4)] ring-1 ring-primary-700/20 transition-all hover:from-primary-500 hover:to-primary-700 hover:shadow-[0_1px_0_0_rgba(255,255,255,0.2)_inset,0_4px_12px_-2px_rgba(2,132,199,0.5)] focus:outline-none focus:ring-4 focus:ring-primary-500/20 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <span>{t('auth.login.submit')}</span>
          )}
        </button>
      </form>
    </div>
  );
};
